import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays and updates a free shipping progress meter.
 *
 * @extends {Component}
 */
class FreeShippingMeter extends Component {
  #isAnimating = false;

  connectedCallback() {
    super.connectedCallback();
    
    // Listen for cart update events
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    
    // Initialize the meter with 0 progress
    this.#setProgressToZero();
    
    // Animate when visible (drawer opens)
    this.#setupVisibilityObserver();
    
    // Debug: Log that meter is initialized
    if (window.location.search.includes('debug=shipping')) {
      console.log('Free shipping meter initialized', {
        threshold: this.dataset.threshold,
        cartTotal: this.dataset.cartTotal
      });
    }
  }

  /**
   * Sets the progress bar to 0 initially
   */
  #setProgressToZero() {
    const progressBar = this.querySelector('.free-shipping-meter__progress-bar');
    if (progressBar) {
      progressBar.style.setProperty('--progress', '0%');
    }
  }

  /**
   * Sets up observer to detect when meter becomes visible
   */
  #setupVisibilityObserver() {
    // Use IntersectionObserver to detect when meter becomes visible
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Meter is visible - animate it
          if (!this.#isAnimating) {
            setTimeout(() => {
              this.#animateProgress();
            }, 100);
          }
        } else {
          // Meter is hidden - reset animation flag so it can animate again next time
          // Don't reset progress value to avoid the flash during drawer open
          this.#isAnimating = false;
        }
      });
    }, {
      threshold: 0.1
    });

    observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * Handles cart update events
   * @param {Event} event - The cart update event
   */
  #handleCartUpdate = (event) => {
    // Wait for morphing to complete, then update
    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.#updateMeter();
      }, 50);
    });
  };

  /**
   * Animates the progress bar from 0 to current value
   */
  #animateProgress() {
    if (this.#isAnimating) return;
    
    this.#isAnimating = true;
    const progressBar = this.querySelector('.free-shipping-meter__progress-bar');
    
    if (progressBar) {
      // Set to 0 first
      progressBar.style.setProperty('--progress', '0%');
      
      // Force a reflow to ensure the 0% is applied
      progressBar.offsetHeight;
      
      // Add animation class
      progressBar.classList.add('free-shipping-meter__progress-bar--animating');
      
      // Update to actual value after a brief delay
      requestAnimationFrame(() => {
        this.#updateMeter(true);
        
        // Remove animation class after animation completes
        setTimeout(() => {
          progressBar.classList.remove('free-shipping-meter__progress-bar--animating');
          this.#isAnimating = false;
        }, 800);
      });
    }
  }

  /**
   * Updates the meter based on current cart total
   * @param {boolean} animate - Whether to animate the progress bar
   */
  #updateMeter(animate = false) {
    const threshold = parseInt(this.dataset.threshold || '5500', 10);
    
    // Get cart total from data attribute (set by Liquid template)
    // This is the most reliable source as it comes directly from Shopify's cart object
    let cartTotal = parseInt(this.dataset.cartTotal || '0', 10);
    
    // Try to get updated cart total from the cart summary after morphing
    // Look for the text-component that contains the cart total in the same section
    const cartSection = this.closest('[id^="shopify-section-"]');
    const cartTotalElement = cartSection 
      ? cartSection.querySelector('[data-cart-subtotal]')
      : document.querySelector('[data-cart-subtotal]');
      
    if (cartTotalElement) {
      const totalText = cartTotalElement.textContent || cartTotalElement.innerText || '';
      // Extract number from money format (e.g., "$55.00" -> 5500, "€55,00" -> 5500)
      // Handle various formats: $55.00, €55,00, 55.00, etc.
      const moneyMatch = totalText.match(/[\d,]+\.?\d*/);
      if (moneyMatch) {
        const value = parseFloat(moneyMatch[0].replace(/,/g, ''));
        const parsedTotal = Math.round(value * 100); // Convert to cents
        // Use parsed value if it's different and seems valid
        if (parsedTotal > 0 && Math.abs(parsedTotal - cartTotal) < 10000) {
          cartTotal = parsedTotal;
        }
      }
    }
    
    // Update the data attribute for future reference
    this.dataset.cartTotal = cartTotal.toString();

    // Calculate progress
    const progressPercentage = Math.min((cartTotal / threshold) * 100, 100);
    const remaining = Math.max(threshold - cartTotal, 0);

    // Update the progress bar
    const progressBar = this.querySelector('.free-shipping-meter__progress-bar');
    if (progressBar) {
      if (animate) {
        // Add smooth transition for animation
        progressBar.style.transition = 'none';
        requestAnimationFrame(() => {
          progressBar.style.transition = '';
        });
      }
      progressBar.style.setProperty('--progress', `${progressPercentage}%`);
      progressBar.setAttribute('aria-valuenow', cartTotal);
    }

    // Update remaining amount text
    const remainingElement = this.querySelector('.free-shipping-meter__remaining');
    if (remainingElement && remaining > 0) {
      // Format money using Shopify's money format
      const remainingFormatted = this.#formatMoney(remaining);
      // Get translation if available, otherwise use default
      const awayText = window.Shopify?.locales?.content?.free_shipping_remaining || '{{ amount }} away';
      remainingElement.textContent = awayText.replace('{{ amount }}', remainingFormatted);
    }

    // Show/hide success message
    const content = this.querySelector('.free-shipping-meter__content');
    if (cartTotal >= threshold && content) {
      content.classList.add('free-shipping-meter__content--complete');
      // Show success message if not already shown
      if (!this.querySelector('.free-shipping-meter__success')) {
        this.#renderSuccessMessage();
      }
    } else {
      content?.classList.remove('free-shipping-meter__content--complete');
      const success = this.querySelector('.free-shipping-meter__success');
      if (success) {
        success.remove();
      }
    }
  }

  /**
   * Formats money amount in cents to currency string
   * @param {number} cents - Amount in cents
   * @returns {string} Formatted money string
   */
  #formatMoney(cents) {
    // Try to use Shopify's money format if available
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    // Fallback: simple dollar format
    return `$${(cents / 100).toFixed(2)}`;
  }

  /**
   * Renders the success message when free shipping is qualified
   */
  #renderSuccessMessage() {
    const content = this.querySelector('.free-shipping-meter__content');
    if (!content) return;

    const successText = window.Shopify?.locales?.content?.free_shipping_qualified || 'You qualify for free shipping!';

    const successHTML = `
      <div class="free-shipping-meter__success">
        <span class="free-shipping-meter__success-icon">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <span class="free-shipping-meter__success-text">
          ${successText}
        </span>
      </div>
    `;
    
    content.innerHTML = successHTML;
  }
}

if (!customElements.get('free-shipping-meter')) {
  customElements.define('free-shipping-meter', FreeShippingMeter);
}

